import React, { useState } from 'react';
import { Check, ChevronDown, ChevronUp, Sparkles, X } from 'lucide-react';
import { useOnboarding, type OnboardingStepKey } from '@/hooks/useOnboarding';

interface OnboardingChecklistProps {
  compactSidebar?: boolean;
  /** Reabre el wizard al hacer clic en "Continuar configuración". */
  onResumeWizard: () => void;
}

const ITEMS: { key: OnboardingStepKey | 'account'; label: string }[] = [
  { key: 'account', label: 'Cuenta creada' },
  { key: 'create_project', label: 'Crear primer proyecto' },
  { key: 'invite_member', label: 'Invitar un miembro' },
  { key: 'enable_ai', label: 'Activar IA predictiva' },
];

export const OnboardingChecklist: React.FC<OnboardingChecklistProps> = ({
  compactSidebar,
  onResumeWizard,
}) => {
  const { steps, completedCount, totalSteps, progress, shouldShowChecklist, dismiss } = useOnboarding();
  const [collapsed, setCollapsed] = useState(false);

  if (!shouldShowChecklist || compactSidebar) return null;

  const isDone = (key: OnboardingStepKey | 'account') => key === 'account' || steps[key];

  return (
    <div
      className="mx-2 mb-3 rounded-xl border border-border bg-card shadow-sm overflow-hidden"
      role="region"
      aria-label="Primeros pasos"
    >
      <div className="flex items-center justify-between px-3 py-2 bg-accent/40">
        <button
          type="button"
          onClick={() => setCollapsed(c => !c)}
          className="flex items-center gap-2 text-xs font-semibold text-foreground flex-1 text-left"
          aria-expanded={!collapsed}
          aria-controls="onboarding-checklist-body"
        >
          <Sparkles className="h-3.5 w-3.5 text-primary" aria-hidden="true" />
          Primeros pasos ({completedCount}/{totalSteps})
          {collapsed ? (
            <ChevronDown className="h-3.5 w-3.5 ml-auto text-muted-foreground" aria-hidden="true" />
          ) : (
            <ChevronUp className="h-3.5 w-3.5 ml-auto text-muted-foreground" aria-hidden="true" />
          )}
        </button>
        <button
          type="button"
          onClick={dismiss}
          className="ml-1 p-1 rounded hover:bg-muted text-muted-foreground"
          aria-label="Descartar guía de primeros pasos"
        >
          <X className="h-3.5 w-3.5" aria-hidden="true" />
        </button>
      </div>

      {/* Barra de progreso */}
      <div className="h-1 bg-muted">
        <div
          className="h-full pm-gradient-bg transition-all duration-500"
          style={{ width: `${progress}%` }}
          role="progressbar"
          aria-valuenow={progress}
          aria-valuemin={0}
          aria-valuemax={100}
          aria-label="Progreso de configuración inicial"
        />
      </div>

      {!collapsed && (
        <div id="onboarding-checklist-body" className="p-3 space-y-2">
          <ul className="space-y-1.5">
            {ITEMS.map(item => {
              const done = isDone(item.key);
              return (
                <li key={item.key} className="flex items-center gap-2 text-xs">
                  <span
                    className={`flex items-center justify-center h-4 w-4 rounded-sm border ${
                      done
                        ? 'bg-primary border-primary text-primary-foreground'
                        : 'border-muted-foreground/40 bg-background'
                    }`}
                    aria-hidden="true"
                  >
                    {done && <Check className="h-3 w-3" />}
                  </span>
                  <span
                    className={done ? 'text-muted-foreground line-through' : 'text-foreground'}
                  >
                    {item.label}
                  </span>
                </li>
              );
            })}
          </ul>

          <button
            type="button"
            onClick={onResumeWizard}
            className="mt-1 w-full text-xs font-semibold text-primary hover:underline text-left"
          >
            Continuar configuración →
          </button>
        </div>
      )}
    </div>
  );
};

export default OnboardingChecklist;
